import { Button } from "@jetblue/kit/button";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";
import { InputRoute } from "@jb-dotcom/dotcom-components/input-route";
import { InputText } from "@jetblue/kit/input-text";

import type { FlightSearchFormLabels } from "../labels";
import type { StatelessFlightSearchFormProperties } from "../stateless";
import type { useFlightSearchForm } from "../hooks/use-flight-search-form";
import type { useForm } from "../use-form";
import { RangeDatePicker } from "@jetblue/kit/date-picker/range";

export type RoundTripProperties = {
  errors: ReturnType<typeof useFlightSearchForm>["errors"];
  formList: ReturnType<typeof useForm>;
  labels: FlightSearchFormLabels;
  odQuery: StatelessFlightSearchFormProperties["odQuery"];
  passengers: { adults: number; children: number; infants: number };
  showPromoCode?: boolean | undefined;
};
export const RoundTrip = ({
  errors,
  formList: {
    endDate,
    first: { departureDate, destination, origin, returnDate },
    modify,
    promoCode,
    setPromoCode,
    setUsePoints,
    startDate,
    swapOriginDestination,
    usePoints,
  },
  labels,
  odQuery,
  showPromoCode,
}: RoundTripProperties) => (
  <div className="flex flex-col gap-1 lg:flex-row">
    <InputRoute
      className="flex-1"
      destination={destination}
      destinationIsRequired={true}
      destinationName="to"
      labels={labels}
      odRouteData={{ destination: odQuery.data, origin: odQuery.data }}
      onDestinationChange={(value) => {
        modify(0, "destination", value);
      }}
      onOriginChange={(value) => {
        modify(0, "origin", value);
      }}
      onSwapClick={() => {
        swapOriginDestination(0);
      }}
      origin={origin}
      originIsRequired
      originName="from"
      showValidation={errors.showValidation}
    />

    <div
      className="flex flex-col gap-1 lg:w-1/2 lg:flex-row"
      data-show-promo-code={showPromoCode}
    >
      <div className="flex flex-row items-start gap-1">
        <RangeDatePicker
          departIsRequired
          endDate={endDate}
          labels={{
            close: labels.close,
            depart: labels.departureDate,
            departError: labels.errors.departureDate,
            done: labels.done,
            reset: labels.reset,
            return: labels.returnDate,
            returnError: labels.errors.returnDate,
          }}
          returnIsRequired
          selectedDates={[departureDate, returnDate]}
          setSelectedDates={[
            (date) => {
              modify(0, "departureDate", date);
            },
            (date) => {
              modify(0, "returnDate", date);
            },
          ]}
          showValidation={errors.showValidation}
          startDate={startDate}
        />
        <input
          className="hidden"
          name="return"
          readOnly
          value={returnDate?.toString() ?? ""}
        />
        <input
          className="hidden"
          name="depart"
          readOnly
          value={departureDate?.toString() ?? ""}
        />

        {showPromoCode && (
          <div className="w-28 min-w-28 md:w-1/3 lg:w-28">
            <InputText
              label={labels.promoCode}
              labelClassName="w-4/5 truncate"
              name="airPromo"
              onChange={(_, value) => {
                setPromoCode(value);
              }}
              value={promoCode}
            />
          </div>
        )}
      </div>

      <div className="mt-2 flex justify-center text-legal md:hidden">
        <InputCheckbox
          isSelected={usePoints}
          onChange={() => {
            setUsePoints((current) => !current);
          }}
        >
          {labels.loyaltyCheckbox}
        </InputCheckbox>
      </div>

      <div className="mt-4 flex justify-center lg:mt-0">
        <Button
          className="h-14 w-full whitespace-nowrap md:w-fit md:min-w-unset lg:rounded-l-none"
          size="large"
          type="submit"
        >
          {labels.submit}
        </Button>
      </div>
    </div>
  </div>
);
